import SimplePeer from 'simple-peer';
import { Socket } from 'socket.io-client';

export type CallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'ended';
export type EndReason = 'rejected' | 'busy' | 'unavailable' | 'hangup' | 'error' | null;

export interface CallerInfo {
  userId: string;
  name: string;
  avatar?: string;
}

export interface CallCallbacks {
  onStateChange: (state: CallState) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onIncomingCall: (callerInfo: CallerInfo) => void;
  onError: (err: Error) => void;
  onEndReason?: (reason: EndReason) => void;
  /** Non-fatal issue — the call proceeds, but the user should be told (e.g. camera unavailable). */
  onWarning?: (message: string) => void;
}

// STUN alone works only on permissive NATs, so a working TURN relay is REQUIRED
// for cross-network calls (e.g. Wi-Fi ↔ mobile data). openrelay.metered.ca's
// free public TURN servers shut down in 2024.
//
// Preferred: configure TURN on the backend (see backend/src/services/turnService.js);
// ICE servers are then fetched from /api/messages/ice-servers before each call
// so credentials never ship in the public bundle.
//
// Fallback (used only if that request fails): REACT_APP_TURN_URL (comma-separated
// list allowed) / REACT_APP_TURN_USERNAME / REACT_APP_TURN_CREDENTIAL baked in at build time.
const FALLBACK_TURN_URLS = (process.env.REACT_APP_TURN_URL || '').split(',').map(u => u.trim()).filter(Boolean);
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  ...(FALLBACK_TURN_URLS.length
    ? [{ urls: FALLBACK_TURN_URLS, username: process.env.REACT_APP_TURN_USERNAME || '', credential: process.env.REACT_APP_TURN_CREDENTIAL || '' }]
    : [])
];

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const ICE_FETCH_TIMEOUT_MS = 5_000;

async function fetchIceServers(): Promise<RTCIceServer[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ICE_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/messages/ice-servers`, { credentials: 'include', signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const servers = data?.data?.iceServers;
    if (!Array.isArray(servers) || servers.length === 0) throw new Error('empty ICE server list');
    // Server has no TURN configured but the build does — keep the build's TURN.
    if (!data.data.turnConfigured && FALLBACK_TURN_URLS.length) return FALLBACK_ICE_SERVERS;
    return servers;
  } catch (err) {
    console.warn('[VideoCall] Could not fetch ICE servers, using fallback:', err);
    return FALLBACK_ICE_SERVERS;
  } finally {
    clearTimeout(timer);
  }
}

const hasTurnServer = (servers: RTCIceServer[]) =>
  servers.some(s => (Array.isArray(s.urls) ? s.urls : [s.urls])
    .some(u => u.startsWith('turn:') || u.startsWith('turns:')));

const VIDEO_SIGNAL_EVENT = 'video-signal';
const CONNECTION_TIMEOUT_MS = 15_000;

export class VideoCallService {
  private socket: Socket | null = null;
  private peer: SimplePeer.Instance | null = null;
  private localStream: MediaStream | null = null;
  private callbacks: CallCallbacks | null = null;
  private currentUserId: string = '';
  private activePeerId: string = '';
  private state: CallState = 'idle';
  private pendingSignals: any[] = [];
  private endedTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionTimer: ReturnType<typeof setTimeout> | null = null;
  private endReason: EndReason = null;
  private iceServers: RTCIceServer[] = FALLBACK_ICE_SERVERS;

  private _onCallRequest = ({ callerId, callerInfo }: { callerId: string; callerInfo: CallerInfo }) => {
    // Glare: we're calling them while they're calling us. Instead of both sides
    // answering "busy", connect. Both sides apply the same rule independently:
    // the lower user id answers the other's call; the higher id ignores the
    // duplicate request and waits for the call-accept that is on its way.
    if (this.state === 'calling' && String(callerId) === this.activePeerId) {
      if (this._isLowerId(this.currentUserId, String(callerId))) {
        this._startPeer(false).then(() => {
          if (this.peer) this.socket?.emit('call-accept', { callerId: this.activePeerId });
        });
      }
      return;
    }
    if (this.state !== 'idle') {
      this.socket?.emit('call-busy', { callerId });
      return;
    }
    this.activePeerId = String(callerId);
    this._setState('ringing');
    this.callbacks?.onIncomingCall(callerInfo);
  };

  private _onCallAccept = async ({ accepterId }: { accepterId: string }) => {
    this.activePeerId = String(accepterId);
    await this._startPeer(true);
  };

  private _onCallReject     = () => { this._endCall('rejected'); };
  private _onCallEnd        = () => { this._endCall('hangup'); };
  private _onCallBusy       = () => { this._endCall('busy'); };
  private _onCallUnavailable = () => { this._endCall('unavailable'); };

  private _onVideoSignal = ({ fromUserId, signal }: { fromUserId: string; signal: any }) => {
    if (String(fromUserId) !== this.activePeerId) return;
    if (this.peer) {
      try { this.peer.signal(signal); } catch (_) {}
    } else {
      this.pendingSignals.push(signal);
    }
  };

  // ── Public API ────────────────────────────────────────────────────────────

  init(socket: Socket, userId: string, callbacks: CallCallbacks) {
    this._unbindSocketEvents();
    this.socket = socket;
    this.currentUserId = userId;
    this.callbacks = callbacks;
    this._bindSocketEvents();

    socket.on('reconnect', () => {
      this._unbindSocketEvents();
      this._bindSocketEvents();
      // Only kill an actively-connected call where media was already flowing —
      // a mid-stream socket flap can't recover. For 'ringing' or 'calling'
      // states, keep the call alive: signalling has its own timeout (15s) and
      // killing the call here means brief socket flaps (very common on mobile
      // — screen on/off, visibilitychange, network handoff) silently abort
      // incoming calls before the user can press Accept.
      if (this.state === 'connected') this._endCall();
    });
  }

  async initiateCall(targetUserId: string, callerInfo: CallerInfo) {
    if (this.state !== 'idle') return;
    this.activePeerId = String(targetUserId);
    this._setState('calling');
    this.socket?.emit('call-request', { targetUserId, callerInfo });
  }

  // Callee: acquire media + create peer first, THEN emit call-accept.
  // This ensures the peer exists and can handle the initiator's first signals
  // without relying on the pendingSignals buffer.
  async acceptCall() {
    if (this.state !== 'ringing') {
      console.warn('[VideoCall] acceptCall ignored — state was', this.state, '(expected "ringing"). Likely caused by a socket flap that ended the call before Accept was pressed.');
      this.callbacks?.onError(new Error('Call no longer active — the connection dropped briefly. Ask them to call again.'));
      return;
    }
    if (!this.activePeerId) {
      console.warn('[VideoCall] acceptCall: activePeerId is empty — cannot route call-accept');
      this.callbacks?.onError(new Error('Call info lost — please ask them to call again.'));
      this._endCall('error');
      return;
    }
    await this._startPeer(false);
    this.socket?.emit('call-accept', { callerId: this.activePeerId });
  }

  rejectCall() {
    if (this.state !== 'ringing') return;
    this.socket?.emit('call-reject', { callerId: this.activePeerId });
    this._peerCleanup();
    this._setState('idle');
  }

  hangUp() {
    const peerId = this.activePeerId;
    this._peerCleanup();
    this.endReason = 'hangup';
    this.callbacks?.onEndReason?.('hangup');
    this._setState('ended');
    if (peerId) this.socket?.emit('call-end', { peerId });
    this._scheduleIdle();
  }

  getLocalStream(): MediaStream | null { return this.localStream; }
  getState(): CallState { return this.state; }
  getEndReason(): EndReason { return this.endReason; }

  // Track which camera is active so we can toggle without reading getSettings()
  private _facingMode: 'user' | 'environment' = 'user';

  /** Flip between front and rear camera during a connected call. */
  async switchCamera(): Promise<void> {
    if (!this.localStream || !this.peer) return;
    const currentTrack = this.localStream.getVideoTracks()[0];
    if (!currentTrack) return;

    const nextFacing = this._facingMode === 'user' ? 'environment' : 'user';

    // Android Chrome won't release the active camera while a track is live, so a
    // second getUserMedia() call for the opposite facing-mode is often ignored
    // and returns the SAME camera. Stop the current track FIRST to free the
    // hardware, then request the new one.
    currentTrack.stop();
    this.localStream.removeTrack(currentTrack);

    // Try `exact` first so we actually get the opposite camera (some Android
    // devices honour `ideal` only as a hint and return what's already warm).
    // Fall back to `ideal` if the device truly lacks a camera on that side.
    let newStream: MediaStream | null = null;
    try {
      newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: nextFacing } },
        audio: false,
      });
    } catch (_) {
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: nextFacing } },
          audio: false,
        });
      } catch (e) {
        console.warn('[VideoCall] switchCamera failed:', e);
        return;
      }
    }

    const newTrack = newStream.getVideoTracks()[0];
    if (!newTrack) return;

    // Replace track in the live peer connection (no renegotiation needed)
    const pc = (this.peer as any)._pc as RTCPeerConnection | undefined;
    if (pc) {
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(newTrack);
    }

    // Stop any extra tracks on the temp stream we don't need, then attach the new
    // video track to the local stream so the self-preview updates.
    // (currentTrack was already stopped/removed before getUserMedia above.)
    newStream.getTracks().forEach(t => { if (t !== newTrack) { try { t.stop(); } catch (_) {} } });
    this.localStream.addTrack(newTrack);

    this._facingMode = nextFacing;
  }

  destroy() {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    if (this.connectionTimer) clearTimeout(this.connectionTimer);
    this._unbindSocketEvents();
    this._peerCleanup();
    this.callbacks = null;
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  /** Deterministic tie-break for simultaneous calls — numeric ids compared as numbers. */
  private _isLowerId(a: string, b: string): boolean {
    const na = Number(a), nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) ? na < nb : a < b;
  }

  private _setState(s: CallState) {
    this.state = s;
    this.callbacks?.onStateChange(s);
  }

  private _scheduleIdle(ms = 2000) {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    this.endedTimer = setTimeout(() => {
      this.endedTimer = null;
      this.endReason = null;
      this.callbacks?.onEndReason?.(null);
      this._setState('idle');
    }, 2000);
  }

  private _endCall(reason: EndReason = 'hangup') {
    this._cancelConnectionTimeout();
    this._peerCleanup();
    this.endReason = reason;
    this.callbacks?.onEndReason?.(reason);
    this._setState('ended');
    // Show error messages longer so the user can read them
    this._scheduleIdle(reason === 'error' ? 8000 : 2000);
  }

  private _startConnectionTimeout() {
    this._cancelConnectionTimeout();
    this.connectionTimer = setTimeout(() => {
      if (this.state !== 'connected') {
        this.callbacks?.onError(new Error(
          hasTurnServer(this.iceServers)
            ? 'Call timed out — could not establish a connection. Check both users have internet, then try again.'
            : 'Call timed out — no TURN server configured. Cross-network calls require a TURN server (see backend turnService.js).'
        ));
        // Use _endCall('error') (not hangUp()) so the UI shows the timeout
        // reason instead of a plain "Call ended" message, but still notify
        // the other side so their call doesn't keep ringing.
        const peerId = this.activePeerId;
        this._endCall('error');
        if (peerId) this.socket?.emit('call-end', { peerId });
      }
    }, CONNECTION_TIMEOUT_MS);
  }

  private _cancelConnectionTimeout() {
    if (this.connectionTimer) { clearTimeout(this.connectionTimer); this.connectionTimer = null; }
  }

  private _bindSocketEvents() {
    if (!this.socket) return;
    this.socket.on('call-request',     this._onCallRequest);
    this.socket.on('call-accept',      this._onCallAccept);
    this.socket.on('call-reject',      this._onCallReject);
    this.socket.on('call-end',         this._onCallEnd);
    this.socket.on('call-busy',        this._onCallBusy);
    this.socket.on('call-unavailable', this._onCallUnavailable);
    this.socket.on(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private _unbindSocketEvents() {
    if (!this.socket) return;
    this.socket.off('call-request',     this._onCallRequest);
    this.socket.off('call-accept',      this._onCallAccept);
    this.socket.off('call-reject',      this._onCallReject);
    this.socket.off('call-end',         this._onCallEnd);
    this.socket.off('call-busy',        this._onCallBusy);
    this.socket.off('call-unavailable', this._onCallUnavailable);
    this.socket.off(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private async _startPeer(initiator: boolean) {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.callbacks?.onError(new Error('Media devices unavailable — site must be served over HTTPS'));
      this._endCall('error');
      return;
    }

    if (navigator.permissions?.query) {
      try {
        const mic = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (mic.state === 'denied') {
          this.callbacks?.onError(new Error('Microphone permission denied — enable it in browser/app settings'));
          this._endCall('error');
          return;
        }
      } catch (_) {}
    }

    // Fetch fresh (short-lived) TURN credentials in parallel with getUserMedia.
    const iceServersPromise = fetchIceServers();

    // Acquire media, then check if the call is still live before proceeding.
    // Without this guard, a stream acquired during a cancelled call leaks
    // and keeps the browser mic/cam indicator on.
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch {
      // Some browsers (notably iPadOS Safari) throw OverconstrainedError/NotReadableError
      // for `video: true` while a more specific constraint succeeds — retry with an
      // explicit facingMode before giving up on video entirely.
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
      } catch {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
          this.callbacks?.onWarning?.('Camera unavailable — connected with audio only. Check camera permissions and try calling again.');
        } catch (err: any) {
          const n = err?.name || '';
          this.callbacks?.onError(new Error(
            n === 'NotAllowedError' ? 'Camera/microphone access denied — click the camera icon in your browser address bar to allow, then call again' :
            n === 'NotFoundError'  ? 'No microphone found on this device' :
            `Could not access camera/microphone (${n || 'unknown error'})`
          ));
          this._endCall('error');
          return;
        }
      }
    }

    this.iceServers = await iceServersPromise;

    if (this.state === 'idle' || this.state === 'ended') {
      stream?.getTracks().forEach(t => t.stop());
      return;
    }
    this.localStream = stream;

    this.peer = new SimplePeer({
      initiator,
      stream: this.localStream,
      trickle: true,
      config: { iceServers: this.iceServers },
    });

    // Start ICE timeout now — media is ready, peer exists, negotiation begins here.
    // Starting it earlier (before getUserMedia) penalises PC/iPad users who see a
    // permission dialog before the stream is granted.
    this._startConnectionTimeout();

    while (this.pendingSignals.length) {
      try { this.peer.signal(this.pendingSignals.shift()); } catch (_) {}
    }

    this.peer.on('signal', (data) => {
      this.socket?.emit(VIDEO_SIGNAL_EVENT, { targetUserId: this.activePeerId, signal: data });
    });

    this.peer.on('stream', (stream) => {
      this._cancelConnectionTimeout();
      this._setState('connected');
      this.callbacks?.onRemoteStream(stream);
    });

    this.peer.on('error', (err) => {
      this.callbacks?.onError(err);
      this._endCall('error');
    });

    this.peer.on('close', () => {
      if (this.state === 'connected' || this.state === 'calling' || this.state === 'ringing') {
        this._endCall();
      }
    });
  }

  private _peerCleanup() {
    this._cancelConnectionTimeout();

    // CRITICAL ORDER: release the mic/camera at the RTCPeerConnection sender level
    // BEFORE destroying the peer. If we just call peer.destroy() with senders still
    // attached, Chrome/Edge can hold the mic indicator (and block other mic users
    // like the browser's speech-recognition API) for 30s+ after the call ends.
    try {
      const pc = (this.peer as any)?._pc as RTCPeerConnection | undefined;
      if (pc) {
        pc.getSenders().forEach(s => {
          try { s.track?.stop(); } catch (_) {}
          try { pc.removeTrack(s); } catch (_) {}
        });
        pc.getReceivers().forEach(r => { try { r.track?.stop(); } catch (_) {} });
      }
    } catch (_) {}

    // Stop the local stream's tracks BEFORE destroy() — defensive in case the
    // stream had tracks that were never wired into the peer connection.
    this.localStream?.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });

    try { this.peer?.destroy(); } catch (_) {}
    this.peer = null;
    this.localStream = null;
    this.activePeerId = '';
    this.pendingSignals = [];
    this._facingMode = 'user'; // reset to front camera for next call
  }
}

export const videoCallService = new VideoCallService();
